import { useMutation, useQuery } from 'react-query';
import { Button, Col, Form, FormGroup, Row, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import TierService from 'api/TierService';
import UploadImage from 'components/UploadImage/UploadImage';
import { GroupFormCheck, Input, TextArea } from 'components/Forms';
import Contents from 'components/Layout/Contents';
import TemplateService from 'api/TemplateService';
import Alert from 'components/Alert/Alert';

export default function AddTier() {
  const templateQuery = useQuery(
    'template-options',
    TemplateService.getAllTemplatesOptions
  );
  const contentBlockQuery = useQuery(
    'contentBlock-options',
    TemplateService.getAllContentBlocksOptions
  );
  const [resetForm, setResetForm] = useState(false);
  const [iconSrc, setIconSrc] = useState(null);
  const { handleSubmit, control, setValue, register } = useForm({
    defaultValues: {
      name: '',
      description: '',
      title: '',
      allowedTemplates: [],
      allowedBlocks: [],
    },
  });
  const [templateError, setTemplateError] = useState('');
  const [contentBlockError, setContentBlockError] = useState('');
  const [savedSuccessfully, setSavedSuccessfully] = useState(null);
  const mutation = useMutation((newTier) => TierService.add(newTier), {
    onSuccess: (response) => {
      if (response?.data) {
        setValue('name', '');
        setValue('title', '');
        setValue('description', '');
        setValue('icon', '');
        setValue('allowedTemplates', []);
        setValue('allowedBlocks', []);
        window.history.pushState(
          response.data,
          '',
          `${window.location.href}?saved=${response.data.id}`
        );
        setSavedSuccessfully(response.data);
      }
    },
  });
  const onSubmit = (formData) => {
    setTemplateError('');
    setContentBlockError('');
    if (formData.allowedTemplates.length === 0) {
      setTemplateError('Please select at least one template.');
      return;
    }
    if (formData.allowedBlocks.length === 0) {
      setContentBlockError('Please select at least one allowed content block.');
      return;
    }
    mutation.mutate(formData);
    setResetForm(true);
  };

  const addNewTierAgain = () => {
    mutation.reset();
    setSavedSuccessfully(null);
    window.history.pushState(null, '', '/admin/tiers/add');
  };

  useEffect(() => {
    window.history.pushState(null, '', '/admin/tiers/add');
  }, []);

  if (savedSuccessfully && mutation.isSuccess) {
    return (
      <div className="saved-details">
        <Alert variant="success" title="Processing">
          New tier added.
        </Alert>
        <div className="d-block mt-4">
          <Table>
            <tbody>
              <tr>
                <td style={{ width: '210px' }} className="text-muted">
                  Name
                </td>
                <td>{savedSuccessfully.name}</td>
                {savedSuccessfully.icon && savedSuccessfully.icon.length > 0 && (
                  <tr>
                    <td rowSpan="5">
                      <img
                        alt={savedSuccessfully.title}
                        src={savedSuccessfully.icon}
                        className="img-thumbnail"
                        style={{
                          width: '128px',
                          height: '128px',
                          objectFit: 'contain',
                          objectPosition: 'center',
                        }}
                      />
                    </td>
                  </tr>
                )}
              </tr>
              <tr>
                <td className="text-muted">Title</td>
                <td>{savedSuccessfully.title}</td>
              </tr>
              <tr>
                <td className="text-muted">Description</td>
                <td>{savedSuccessfully.description}</td>
              </tr>
              <tr>
                <td className="text-muted">Templates</td>
                <td>
                  {savedSuccessfully.allowedTemplates.map((t, i) => (
                    <span
                      className="btn btn-light me-2"
                      key={`tpl-${i.toString()}`}
                    >
                      {t}
                    </span>
                  ))}
                </td>
              </tr>
              <tr>
                <td className="text-muted">Allowed Content Blocks</td>
                <td>
                  {savedSuccessfully.allowedBlocks.map((b) => {
                    const f = contentBlockQuery.data.find((d) => d.value === b);
                    if (f) {
                      return (
                        <span className="btn btn-light me-2" key={f.value}>
                          {f.label}
                        </span>
                      );
                    }
                    return '';
                  })}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="border-0">
                  <div className="d-flex py-3">
                    <Link to="/tiers">Return to tier list</Link>
                    <Button
                      variant="primary"
                      className="btn btn-primary ms-auto"
                      onClick={addNewTierAgain}
                    >
                      Add another tier
                    </Button>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <Contents title="Add Tier">
      <Form
        onSubmit={handleSubmit(onSubmit)}
        id="frmAddTier"
        method="POST"
        encType="multipart/form-data"
      >
        {mutation.isLoading && (
          <Alert
            variant="info"
            title="Processing"
            iconProps={{ spin: true, pulse: true }}
          >
            Creating new tier...
          </Alert>
        )}
        {mutation.isError && (
          <Alert variant="danger" title="Error">
            An error occurred: {mutation.error.message}
          </Alert>
        )}
        {mutation.isSuccess && (
          <Alert variant="success" title="Processing">
            New tier added.
          </Alert>
        )}
        {templateError && templateError.length > 0 && (
          <p className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationCircle} /> {templateError}
          </p>
        )}
        {contentBlockError && contentBlockError.length > 0 && (
          <p className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationCircle} /> {contentBlockError}
          </p>
        )}
        <Row className="mb-sm-3">
          <Col sm={12} xl={8} md={8}>
            <FormGroup>
              <Input
                label="Name"
                control={control}
                name="name"
                rules={{ required: true }}
                disabled={mutation.isLoading}
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Title"
                control={control}
                name="title"
                rules={{ required: true }}
                disabled={mutation.isLoading}
              />
            </FormGroup>
            <FormGroup>
              <TextArea
                label="Description"
                control={control}
                rules={{ required: true }}
                name="description"
              />
            </FormGroup>
            <FormGroup>
              <GroupFormCheck
                label="Templates"
                name="allowedTemplates"
                control={control}
                items={templateQuery.data}
                register={register}
                disabled={mutation.isLoading || templateQuery.isFetching}
              />
            </FormGroup>
            <FormGroup>
              <GroupFormCheck
                label="Allowed Content Blocks"
                name="allowedBlocks"
                control={control}
                register={register}
                items={contentBlockQuery.data}
                disabled={mutation.isLoading || contentBlockQuery.isFetching}
              />
            </FormGroup>
          </Col>
          <Col sm={12} xl={4} md={4}>
            <div className="d-flex justify-content-lg-end justify-content-md-end justify-content-sm-center justify-content-center mt-sm-3">
              {!resetForm && (
                <UploadImage
                  name="file"
                  title="Icon"
                  register={register}
                  reset={resetForm}
                  src={iconSrc}
                  setIconSrc={setIconSrc}
                />
              )}
            </div>
          </Col>
        </Row>
        <FormGroup>
          <Button
            variant="primary"
            className="btn-pmi"
            type="submit"
            disabled={mutation.isLoading}
          >
            Save
          </Button>
          <Link as={Button} to="/tiers" className="d-inline-block ms-3">
            Cancel
          </Link>
        </FormGroup>
      </Form>
    </Contents>
  );
}
